import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Clock, Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { fireEvent } from '@/lib/analytics';
import { format } from 'date-fns';

const CONSULTING_1HR_URL = 'https://invoice.zoho.com/pay/1hr-link';
const CONSULTING_5HR_URL = 'https://invoice.zoho.com/pay/5hr-link';
const OWNER_CONSULTING_RATE = 200;

export default function AccountConsulting() {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/account/login');
      }
    });

    fireEvent('consulting_view');
  }, [navigate]);

  const { data: userId } = useQuery({
    queryKey: ['user-id'],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.email) return null;

      const { data: user } = await supabase
        .from('users')
        .select('id')
        .eq('email', session.user.email.toLowerCase().trim())
        .maybeSingle();

      return user?.id || null;
    },
  });

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['consulting-orders', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('consulting_orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const handlePurchaseClick = (packageType: '1HR' | '5HR') => {
    fireEvent('consulting_click', { package: packageType });
    const url = packageType === '1HR' ? CONSULTING_1HR_URL : CONSULTING_5HR_URL;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'canceled':
      case 'refunded':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Buy Consulting Time with Adam</h1>
          <p className="text-muted-foreground">
            Purchase consulting packages to work directly with Adam on your domain strategy and
            business needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />1 Hour Session
              </CardTitle>
              <CardDescription>Perfect for quick strategy consultations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-3xl font-bold text-foreground">
                  ${OWNER_CONSULTING_RATE}
                  <span className="text-sm text-muted-foreground font-normal ml-2">per hour</span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Domain acquisition strategy</li>
                  <li>• Portfolio review and optimization</li>
                  <li>• Pricing and negotiation guidance</li>
                  <li>• Market insights and trends</li>
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={() => handlePurchaseClick('1HR')} className="w-full">
                Purchase 1 Hour <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>

          <Card className="hover:shadow-lg transition-shadow border-primary">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-primary" />5 Hour Package
                  </CardTitle>
                  <CardDescription>Best value for comprehensive projects</CardDescription>
                </div>
                <Badge variant="secondary" className="text-xs">
                  Save 10%
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-3xl font-bold text-foreground">
                  ${OWNER_CONSULTING_RATE * 5 * 0.9}
                  <span className="text-sm text-muted-foreground font-normal ml-2">5 hours</span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Everything in 1 Hour Session</li>
                  <li>• Extended strategic planning</li>
                  <li>• Implementation support</li>
                  <li>• Follow-up and refinement</li>
                  <li>• Priority scheduling</li>
                </ul>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={() => handlePurchaseClick('5HR')} className="w-full">
                Purchase 5 Hours <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Order History</CardTitle>
            <CardDescription>View your consulting package purchases</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No orders yet. Purchase a package above to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Package
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Hours
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Amount
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      fireEvent('consulting_order_view', {
                        orderId: order.id,
                        package: order.package,
                      });
                      return (
                        <tr key={order.id} className="border-b hover:bg-muted/50">
                          <td className="py-3 px-4 font-medium">{order.package}</td>
                          <td className="py-3 px-4">{order.hours}h</td>
                          <td className="py-3 px-4">${order.amount || 0}</td>
                          <td className="py-3 px-4">
                            <Badge variant={getStatusBadgeVariant(order.status)}>
                              {order.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {format(new Date(order.created_at), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
